import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../app.config';
import { APIResponse } from '../common/common.model';
import { mapList } from '../common/rest.operators';
import { HierarchyUserInfoItem } from '../insight/config.insight.model';
import {
    CacheDateItem,
    CacheDateItemModelSetTransformer,
    validateCacheDate,
} from '../performance-data/cache-date.pd.model';
import {
    DataFilterOptions,
    DataFilterOptionsModelTransformer,
} from '../performance-data/data-filter-options.pd.model';
import { ExtMetricDataItem } from '../performance-data/metric.pd.model';
import { PeriodType } from '../widget/date-range.widget.model';

import { CardConfig } from './models/card-config.model';
import { PerformanceMetricData } from './models/performance-metric-data.model';
import { MetricModelTransformer } from './transformers/metric-model.transformer';

@Injectable()
export class MyPerformanceCardService {
    constructor(
        private readonly _http: HttpClient,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
    ) {}

    listHierarchyUserInfo(
        userIds: number[],
    ): Observable<HierarchyUserInfoItem[]> {
        return this._http
            .post<APIResponse<HierarchyUserInfoItem[]>>(
                `${this._appConfig.API_ENDPOINTS.INSIGHT}/config/hierarchyUserInfo`,
                {
                    userIds,
                },
            )
            .pipe(
                mapList(),
                catchError(() => of([])),
            );
    }

    listData(opts: DataFilterOptions): Observable<PerformanceMetricData[]> {
        const transformed =
            new DataFilterOptionsModelTransformer().reverseTransform(opts);

        return this._http
            .post(
                `${this._appConfig.API_ENDPOINTS.PERFORMANCE_DATA}/performancedata/data/${opts.viewType}/${opts.periodType}`,
                transformed,
            )
            .pipe(
                mapList(),
                map((metrics: ExtMetricDataItem[]) => {
                    const transformer = new MetricModelTransformer(
                        opts.viewType,
                    );
                    return transformer.transformList(metrics);
                }),
                catchError((error) => {
                    console.error(error);
                    throw new Error(
                        'performancedata/data/:viewTypeId/:periodTypeId failed',
                    );
                }),
            );
    }

    listCacheDates(
        periodType: PeriodType,
        userId?: number,
    ): Observable<CacheDateItem[]> {
        let params = new HttpParams();
        if (userId != null) {
            params = params.set('userId', String(userId));
        }
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.PERFORMANCE_DATA}/performancedata/datelist/${periodType}`,
                { params },
            )
            .pipe(
                mapList(new CacheDateItemModelSetTransformer()),
                tap((model: CacheDateItem[]) => validateCacheDate(model)),
                catchError((error) => {
                    console.error(error);
                    throw new Error(
                        'performancedata/datelist/:periodTypeId failed',
                    );
                }),
            );
    }

    retrieveCardConfig(): Observable<CardConfig[]> {
        const params = new HttpParams().set('components', 'cardconfig');

        return this._http
            .get<CardConfig[]>(
                `${this._appConfig.API_ENDPOINTS.INSIGHT}/card/config`,
                { params },
            )
            .pipe(
                catchError((error) => {
                    console.error('Failed to fetch Card Config', error);
                    return of([]);
                }),
            );
    }
}
